import axios, { AxiosError } from 'axios';
import type {
  ApiResponse,
  AuthResponse,
  Todo,
  TodoCreateInput,
  TodoUpdateInput,
  TodosResponse,
  Category,
  CategoryCreateInput,
  CategoryUpdateInput,
  CategoriesResponse,
  List,
  ListCreateInput,
  ListUpdateInput,
  ListsResponse,
  Swatch,
  SwatchCreateInput,
  SwatchUpdateInput,
  SwatchesResponse,
  UsersSearchResponse,
  UserPreferences,
} from '../types';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const NO_REFRESH_ROUTES = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

let isRefreshing = false;
let refreshPromise: Promise<unknown> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse>) => {
    const url = error.config?.url || '';
    const isNoRefreshRoute = NO_REFRESH_ROUTES.some((route) => url.includes(route));
    if (error.response?.status === 401 && !isNoRefreshRoute) {
      // Deduplicate concurrent refresh attempts
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = api.post('/auth/refresh').finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
      }
      try {
        await refreshPromise;
        return api.request(error.config!);
      } catch {
        // Let the error propagate — AuthContext will set user to null
        // and React router will show the login page. No hard redirect.
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  register: async (email: string, password: string, name: string) => {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/register', {
      email,
      password,
      name,
    });
    return response.data;
  },

  login: async (email: string, password: string) => {
    const response = await api.post<ApiResponse<AuthResponse>>('/auth/login', {
      email,
      password,
    });
    return response.data;
  },

  logout: async () => {
    const response = await api.post<ApiResponse>('/auth/logout');
    return response.data;
  },

  getMe: async () => {
    const response = await api.get<ApiResponse<AuthResponse>>('/auth/me');
    return response.data;
  },

  updateProfile: async (data: { name?: string; preferences?: Partial<UserPreferences> }) => {
    const response = await api.put<ApiResponse<AuthResponse>>('/auth/profile', data);
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.put<ApiResponse>('/auth/password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  verifyEmail: async (token: string) => {
    const response = await api.get<ApiResponse>('/auth/verify-email', {
      params: { token },
    });
    return response.data;
  },

  resendVerification: async () => {
    const response = await api.post<ApiResponse>('/auth/resend-verification');
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post<ApiResponse>('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, password: string) => {
    const response = await api.post<ApiResponse>('/auth/reset-password', {
      token,
      password,
    });
    return response.data;
  },

  exportData: async () => {
    const response = await api.get('/auth/export', { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `todo-export-${new Date().toISOString().replace(/[T:.]/g, '-').slice(0, 19)}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  deleteAccount: async () => {
    const response = await api.delete<ApiResponse>('/auth/account', {
      data: { confirmation: 'DELETE' },
    });
    return response.data;
  },

  forgetData: async () => {
    const response = await api.delete<ApiResponse>('/auth/forget', {
      data: { confirmation: 'FORGET' },
    });
    return response.data;
  },
};

export const todosApi = {
  list: async (params?: {
    completed?: boolean;
    category?: string;
    listId?: string;
    priority?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await api.get<ApiResponse<TodosResponse>>('/todos', { params });
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<ApiResponse<{ todo: Todo }>>(`/todos/${id}`);
    return response.data;
  },

  create: async (data: TodoCreateInput) => {
    const response = await api.post<ApiResponse<{ todo: Todo }>>('/todos', data);
    return response.data;
  },

  update: async (id: string, data: TodoUpdateInput) => {
    const response = await api.put<ApiResponse<{ todo: Todo }>>(`/todos/${id}`, data);
    return response.data;
  },

  duplicate: async (id: string, listId: string, category?: string | null) => {
    const response = await api.post<ApiResponse<{ todo: Todo }>>(
      `/todos/${id}/duplicate`,
      { listId, category: category || null }
    );
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/todos/${id}`);
    return response.data;
  },

  toggle: async (id: string) => {
    const response = await api.patch<ApiResponse<{ todo: Todo }>>(`/todos/${id}/toggle`);
    return response.data;
  },

  share: async (id: string, email: string, permission: 'view' | 'edit') => {
    const response = await api.post<ApiResponse<{ todo: Todo }>>(`/todos/${id}/share`, {
      email,
      permission,
    });
    return response.data;
  },

  unshare: async (id: string, userId: string) => {
    const response = await api.delete<ApiResponse>(`/todos/${id}/share/${userId}`);
    return response.data;
  },

  reorder: async (todos: Array<{ id: string; position: number }>) => {
    const response = await api.patch<ApiResponse>('/todos/reorder', { todos });
    return response.data;
  },

  addSubtask: async (todoId: string, title: string) => {
    const response = await api.post<ApiResponse<{ todo: Todo }>>(
      `/todos/${todoId}/subtasks`,
      { title }
    );
    return response.data;
  },

  toggleSubtask: async (todoId: string, subtaskId: string) => {
    const response = await api.patch<ApiResponse<{ todo: Todo }>>(
      `/todos/${todoId}/subtasks/${subtaskId}/toggle`
    );
    return response.data;
  },

  deleteSubtask: async (todoId: string, subtaskId: string) => {
    const response = await api.delete<ApiResponse<{ todo: Todo }>>(
      `/todos/${todoId}/subtasks/${subtaskId}`
    );
    return response.data;
  },

  reorderSubtasks: async (todoId: string, subtasks: Array<{ id: string; position: number }>) => {
    const response = await api.patch<ApiResponse<{ todo: Todo }>>(
      `/todos/${todoId}/subtasks/reorder`,
      { subtasks }
    );
    return response.data;
  },

  renameSubtask: async (todoId: string, subtaskId: string, title: string) => {
    const response = await api.patch<ApiResponse<{ todo: Todo }>>(
      `/todos/${todoId}/subtasks/${subtaskId}/rename`,
      { title }
    );
    return response.data;
  },

  listTags: async () => {
    const response = await api.get<ApiResponse<string[]>>('/todos/tags');
    return response.data;
  },

  uploadImage: async (todoId: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post<ApiResponse<{ todo: Todo }>>(
      `/todos/${todoId}/image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  deleteImage: async (todoId: string, filename: string) => {
    const response = await api.delete<ApiResponse<{ todo: Todo }>>(
      `/todos/${todoId}/image/${encodeURIComponent(filename)}`
    );
    return response.data;
  },
};

export const categoriesApi = {
  list: async (params?: { listId?: string }) => {
    const response = await api.get<ApiResponse<CategoriesResponse>>('/categories', { params });
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<ApiResponse<{ category: Category }>>(`/categories/${id}`);
    return response.data;
  },

  create: async (data: CategoryCreateInput) => {
    const response = await api.post<ApiResponse<{ category: Category }>>('/categories', data);
    return response.data;
  },

  update: async (id: string, data: CategoryUpdateInput) => {
    const response = await api.put<ApiResponse<{ category: Category }>>(`/categories/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/categories/${id}`);
    return response.data;
  },

  share: async (id: string, email: string, permission: 'view' | 'edit') => {
    const response = await api.post<ApiResponse<{ category: Category }>>(`/categories/${id}/share`, {
      email,
      permission,
    });
    return response.data;
  },

  unshare: async (id: string, userId: string) => {
    const response = await api.delete<ApiResponse>(`/categories/${id}/share/${userId}`);
    return response.data;
  },
};

export const listsApi = {
  list: async () => {
    const response = await api.get<ApiResponse<ListsResponse>>('/lists');
    return response.data;
  },

  get: async (id: string) => {
    const response = await api.get<ApiResponse<{ list: List }>>(`/lists/${id}`);
    return response.data;
  },

  create: async (data: ListCreateInput) => {
    const response = await api.post<ApiResponse<{ list: List }>>('/lists', data);
    return response.data;
  },

  update: async (id: string, data: ListUpdateInput) => {
    const response = await api.put<ApiResponse<{ list: List }>>(`/lists/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/lists/${id}`);
    return response.data;
  },

  share: async (id: string, email: string, permission: 'view' | 'edit') => {
    const response = await api.post<ApiResponse<{ list: List }>>(`/lists/${id}/share`, {
      email,
      permission,
    });
    return response.data;
  },

  unshare: async (id: string, userId: string) => {
    const response = await api.delete<ApiResponse>(`/lists/${id}/share/${userId}`);
    return response.data;
  },

  reorder: async (lists: Array<{ id: string; position: number }>) => {
    const response = await api.patch<ApiResponse>('/lists/reorder', { lists });
    return response.data;
  },
};

export const swatchesApi = {
  list: async () => {
    const response = await api.get<ApiResponse<SwatchesResponse>>('/swatches');
    return response.data;
  },

  create: async (data: SwatchCreateInput) => {
    const response = await api.post<ApiResponse<{ swatch: Swatch }>>('/swatches', data);
    return response.data;
  },

  update: async (id: string, data: SwatchUpdateInput) => {
    const response = await api.put<ApiResponse<{ swatch: Swatch }>>(`/swatches/${id}`, data);
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete<ApiResponse>(`/swatches/${id}`);
    return response.data;
  },

  reorder: async (swatches: Array<{ id: string; position: number }>) => {
    const response = await api.patch<ApiResponse>('/swatches/reorder', { swatches });
    return response.data;
  },
};

export const usersApi = {
  search: async (email: string) => {
    const response = await api.get<ApiResponse<UsersSearchResponse>>('/users/search', {
      params: { email },
    });
    return response.data;
  },
};

export default api;
